import { Component, Inject } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { AppState } from "@app/app.reducer";
import { ContactService } from "@app/core/ngrx/entity-services/contact.service";
import * as RouterActions from "@app/core/ngrx/router/router.actions";
import { Contact } from "@app/models";
import { getFullName } from "@app/models/contact";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";

export type RemoveContactAction = "deactivate" | "anonymize";
type RemoveContactStatus = "idle" | "working" | "success" | "failed";

@Component({
  selector: "app-remove-contact-modal",
  templateUrl: "./remove-contact-modal.component.html",
  styleUrls: ["./remove-contact-modal.component.scss"],
})
export class RemoveContactModalComponent {
  form: FormGroup;
  contactName: string;
  status: RemoveContactStatus = "idle";
  actions = [
    { label: "remove_contact_deactivate", value: "deactivate" },
    { label: "remove_contact_anonymize", value: "anonymize" },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { contact: Contact },
    public dialogRef: MatDialogRef<RemoveContactModalComponent>,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private contactService: ContactService
  ) {
    this.contactName = getNameToConfirm(data.contact);
    this.form = this.fb.group({
      action: ["deactivate"],
      confirmName: [""],
    });

    this.form.valueChanges.subscribe(() => {
      if (this.status === "failed") {
        this.status = "idle";
      }
    });

    // MatDialog destroys this component before afterClosed() emits, so this
    // subscription must not end on destroy.
    this.dialogRef.afterClosed().subscribe(() => {
      if (this.status === "success") {
        this.store.dispatch(
          RouterActions.go({ path: ["/crm", "dashboard", "todo-list"] })
        );
      }
    });
  }

  get action(): RemoveContactAction {
    return this.form.get("action").value;
  }

  get nameMatches(): boolean {
    return (
      normalize(this.form.get("confirmName").value) ===
      normalize(this.contactName)
    );
  }

  submit(): void {
    if (!this.nameMatches || this.status === "working") {
      return;
    }

    const contactId = this.data.contact.contactId;
    const request$: Observable<unknown> =
      this.action === "anonymize"
        ? this.contactService.anonymize(contactId)
        : this.contactService.deactivate(contactId);

    this.status = "working";
    this.dialogRef.disableClose = true;
    this.form.disable({ emitEvent: false });

    request$.subscribe({
      next: () => {
        this.dialogRef.disableClose = false;
        this.status = "success";
      },
      error: () => {
        this.dialogRef.disableClose = false;
        this.form.enable({ emitEvent: false });
        this.status = "failed";
      },
    });
  }

  closeModal(): void {
    this.dialogRef.close();
  }
}

function getNameToConfirm(contact: Contact): string {
  return (
    getFullName(contact).trim() || contact.fullName?.trim() || contact.contactId
  );
}

function normalize(text: string): string {
  return (text ?? "").trim().replace(/\s+/g, " ").toLocaleLowerCase();
}
